import { z } from 'zod';

import { CURRENCIES } from '../../types';
import { VERTICAL } from '../../helpers/verticals';

export const getPhoneNumberSchema = z.object({
  phoneNumber: z.string(),
  campaignId: z.string().optional(),
});

export type GetPhoneNumber = z.infer<typeof getPhoneNumberSchema>;

const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalSchema>;
type Json = Literal | { [key: string]: Json } | Json[];
const JsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([literalSchema, z.array(JsonSchema), z.record(JsonSchema)]),
);

const ReviewListSchema = z.object({
  authorName: z.string(),
  rating: z.number(),
  relativeTimeDescription: z.string(),
  text: z.string(),
});

const GoogleReviewsSchema = z.object({
  rating: z.string(),
  ratingCategory: z.string().optional(),
  url: z.string(),
  userRatingsTotal: z.string(),
  reviewList: z.array(ReviewListSchema),
  geometry: z.object({
    location: z.object({
      lat: z.number(),
      lng: z.number(),
    }),
    viewport: z.object({
      northeast: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
      southwest: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    }),
  }),
  displayReviews: z.boolean().optional(),
  cid: z.string(),
});

const IBusinessAddressSchema = z.object({
  addressLocality: z.string().optional(),
  addressRegion: z.string().optional(),
  streetAddress: z.string().optional(),
  postalCode: z.string().optional(),
});

const EnhancedOpeningHoursSchema = z.object({
  days: z.array(
    z.object({
      day: z.enum([
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ]),
      openTime: z.string().optional(),
      closeTime: z.string().optional(),
      appointmentOnly: z.boolean().optional(),
      appointmentOnlyText: z.string().optional(),
    }),
  ),
});

const DealerSummarySchema = z.object({
  id: z.number(),
  address: z.string(),
  enhancedAddress: IBusinessAddressSchema.optional(),
  description: z.string().optional(),
  type: z.string().optional(),
  openingHours: z.string().optional(),
  enhancedOpeningHours: EnhancedOpeningHoursSchema.optional(),
  longitude: z.number().optional(),
  latitude: z.number().optional(),
  websiteURL: z.string().optional(),
  otherAds: z.boolean().optional(),
  totalAds: z.number().optional(),
  establishedYear: z.number().optional(),
  franchiseType: z.string().optional(),
  showMiniShowroom: z.boolean().optional(),
  showDealerCard: z.boolean().optional(),
  showEnquiryFormTradeIn: z.boolean().optional(),
  showroomUrl: z.string(),
  closedNow: z.boolean().optional(),
  forecourtPhoto: z
    .object({
      small: z.string(),
      large: z.string(),
      medium: z.string(),
    })
    .optional(),
  name: z.string(),
  logo: z
    .object({
      small: z.string().optional(),
      medium: z.string().optional(),
    })
    .optional(),
  encryptedPhone: z.string().optional(),
  franchisesDisplay: z.string().optional(),
  franchiseCount: z.number().optional(),
  level: z.string().optional(),
  trustedDealer: z.boolean().optional(),
  googleReview: GoogleReviewsSchema.optional(),
});

const PhotoViewSchema = z.object({
  small: z.string(),
  small2: z.string(),
  medium: z.string(),
  large: z.string(),
  xLarge: z.string(),
  smallWebp: z.string(),
  small2Webp: z.string(),
  mediumWebp: z.string(),
  largeWebp: z.string(),
  xLargeWebp: z.string(),
  sortOrder: z.number().optional(),
  name: z.string().optional(),
  id: z.number().optional(),
});

const IOldPriceSchema = z.object({
  oldPrice: z.string().nullable().optional(),
  showOldPrice: z.boolean().optional(),
});

const IManufacturerApprovedBadgeSchema = z.object({
  badgeUrl: z.string().optional(),
  manufacturer: z.string().optional(),
});

const ICardPaymentSchema = z.object({
  stripeAccountId: z.string().optional(),
  quantity: z.string().optional(),
  shippingFee: z.string().optional(),
  shippingAvailable: z.boolean().optional(),
  cardPaymentType: z.string().optional(),
  acceptPayment: z.boolean().optional(),
});

const SectionViewSchema = z.object({
  containsSubsections: z.boolean().optional(),
  displayName: z.string().optional(),
  name: z.string().optional(),
  subdomain: z.string().optional(),
  description: z.string().optional(),
  id: z.number().optional(),
  title: z.string().optional(),
  xtn2: z.number().optional(),
});

const AdSummarySchema = z.object({
  id: z.number(),
  userId: z.number().optional(),
  state: z.string().optional(),
  emailResponse: z.boolean().optional(),
  phoneResponse: z.boolean().optional(),
  bumpable: z.boolean().optional(),
  spotlightable: z.boolean().optional(),
  header: z.string(),
  description: z.string().optional(),
  price: z.string().optional(),
  oldPriceView: IOldPriceSchema.optional(),
  currency: z.nativeEnum(CURRENCIES),
  keyInfo: z.array(z.string()).optional(),
  age: z.string().optional(),
  county: z.string().optional(),
  financeSummary: z
    .object({
      price: z.string(),
      financeProvider: z.string().optional(),
    })
    .optional(),
  photos: z.array(PhotoViewSchema).optional(),
  userSaved: z.boolean().optional(),
  spotlight: z.boolean().optional(),
  friendlyUrl: z.string().optional(),
  mediaCount: z.number().optional(),
  dealer: DealerSummarySchema.optional(),
  manufacturerApprovedBadge: IManufacturerApprovedBadgeSchema.optional(),
  meritsV2: z
    .array(
      z.object({
        field: z.string(),
        text: z.string(),
        description: z.string().optional(),
        colour: z.string().optional(),
      }),
    )
    .optional(),
  herdwatchVerified: z.boolean().optional(),
  specialOffer: z.string().optional(),
  greenlightVerified: z.boolean().optional(),
  cardPayments: z.array(ICardPaymentSchema).optional(),
  deliveryAvailable: z.boolean().optional(),
  imageAlt: z.string().optional(),
  wanted: z.boolean().optional(),
  dealerId: z.number().optional(),
  displayAttributes: z
    .array(
      z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.string(),
        badge: z
          .object({
            showBadge: z.boolean().optional(),
          })
          .optional(),
      }),
    )
    .optional(),
  section: SectionViewSchema.optional(),
  year: z.string().optional(),
  priceOnRequest: z.boolean().optional(),
  countyTown: z.string().optional(),
});

const GtmSchema = z.intersection(
  z.record(z.string(), JsonSchema.or(z.undefined())),
  z.object({
    event: z.string().or(z.array(z.string())).optional(),
  }),
);

const DataTargetingPropsSchema = z.object({
  gtm: GtmSchema,
  dfp: z.record(
    z.string(),
    z.union([
      z.string(),
      z.tuple([z.string()]),
      z.tuple([z.null()]),
      z.tuple([]),
    ]),
  ),
});

export const HomePageResponseSchema = z.object({
  dataTargetingProps: DataTargetingPropsSchema,
  recentAds: z.array(AdSummarySchema),
  recommendedAds: z.array(AdSummarySchema),
  siteFocus: z.nativeEnum(VERTICAL),
  featuredDealer: z
    .object({
      id: z.number(),
      type: z.string(),
      display: z.string(),
      ads: z.array(AdSummarySchema),
      totalAdCount: z.number(),
      showroomUrl: z.string(),
      logoUrl: z.string(),
      largeLogoUrl: z.string().optional(),
      dealerType: z.string(),
      forecourtPhoto: z
        .object({
          small: z.string(),
          large: z.string(),
          medium: z.string(),
        })
        .optional(),
    })
    .nullable()
    .optional(),
  recentAdsUrl: z.string().nullable(),
  seoLinks: z.object({
    internalLinks: z.array(
      z.object({
        text: z.string(),
        url: z.string(),
        make: z.string().optional(),
        model: z.string().optional(),
        year: z.string().optional(),
        county: z.string().optional(),
        fuelType: z.string().optional(),
        sortKey: z.number().optional(),
      }),
    ),
  }),
  sections: z.array(
    z.object({
      name: z.string(),
      displayName: z.string(),
      id: z.number().optional(),
    }),
  ),
  dashLinks: z.array(
    z.object({
      name: z.string(),
      displayName: z.string(),
      count: z.number(),
      icon: z.string(),
      url: z.string(),
    }),
  ),
  lifestyleLinks: z
    .array(
      z.object({
        name: z.string(),
        displayName: z.string(),
        count: z.number(),
        url: z.string(),
      }),
    )
    .optional(),
  adCount: z.object({
    carsTotalAdCount: z.number().optional(),
  }),
});
